import { computed } from 'vue';
import { useStore } from 'vuex';

const INSURANCE = {
  REQUEST_COI: 'request-coi',
  COI_PENDING: 'coi-pending',
  REQUEST_CARGO: 'request-cargo',
  CARGO_PENDING: 'cargo-pending',
};

const CRITICAL_STATUSES = [
  'no_eld_vin_matches_on_coi',
  'no_vins_on_coi',
  'missing_vins_on_coi',
  'bipd_not_on_file',
  'no_coi',
  'no_active_vehicles_found_on_eld',
];

const WARNING_STATUSES = ['material_difference_between_coi_and_eld'];

const resolveInsurance = (a) => {
  // 1. Add COI
  if (!a.has_active_coi_on_file && !a.has_pending_coi_request)
    return INSURANCE.REQUEST_COI;

  // 2. Pending COI
  if (a.has_pending_coi_request && !a.has_active_coi_on_file)
    return INSURANCE.COI_PENDING;

  if (a.is_exempt_from_cargo_policy_requests) {
    return INSURANCE.CARGO_ACTIVE; // Exempt from cargo policy requests
  }

  // 3-5 Cargo path (COI active)
  if (!a.cargo_policy_on_file_and_active && !a.has_pending_cargo_policy_request)
    return INSURANCE.REQUEST_CARGO;

  // Here we want to use cargo-pending because we want to grant access to TFX without requiring Cargo to be active.
  return INSURANCE.CARGO_PENDING;
};

export function useExchangeAccess() {
  const store = useStore();

  const access = computed(
    () => store.state.users?.userLoadBoardAccess?.user ?? {}
  );

  const eldStatus = computed(
    () => store.state.carriers.carrierVehiclesForCarrier?.eld_status
  );

  const showCargoPolicyBanner = computed(() => {
    const pending = access.value.has_pending_cargo_policy_request;
    const ts = access.value.most_recent_cargo_policy_request_time;

    if (!pending || !ts) return false;

    const createdAt = new Date(ts);
    const now = new Date();
    const diffInHours =
      (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);

    return diffInHours > 72;
  });

  const displayEquipmentModal = computed(() => {
    return (
      !!eldStatus.value &&
      [...CRITICAL_STATUSES, ...WARNING_STATUSES].includes(eldStatus.value) &&
      entrywayIsComplete.value
    );
  });

  const entrywayIsComplete = computed(() => {
    const s = generalStatus.value;
    return (
      s.idv == 'completed' &&
      s.eld == 'completed' &&
      s.insurance == 'cargo-pending'
    );
  });

  const bannerType = computed(() => {
    if (CRITICAL_STATUSES.includes(eldStatus.value)) return 'critical';
    if (WARNING_STATUSES.includes(eldStatus.value)) return 'warning';
    return null;
  });

  const displayDismissButton = computed(() => {
    return WARNING_STATUSES.includes(eldStatus.value);
  });

  const insuranceStatus = computed(() => resolveInsurance(access.value));

  const generalStatus = computed(() => ({
    idv: access.value.has_completed_idv ? 'completed' : 'not-started',
    eld: access.value.has_eld_connected_and_active
      ? 'completed'
      : 'not-started',
    insurance: insuranceStatus.value,
  }));

  const nextStepToComplete = computed(() => {
    if (generalStatus.value.idv === 'not-started')
      return 'Protect Your Identity Exchange';
    if (generalStatus.value.eld === 'not-started')
      return 'Connect Your ELD Exchange';
    if (generalStatus.value.insurance !== 'cargo-pending')
      return 'File Your Insurance Exchange';
    return 'Load Board';
  });

  return {
    bannerType,
    generalStatus,
    insuranceStatus,
    nextStepToComplete,
    entrywayIsComplete,
    displayDismissButton,
    displayEquipmentModal,
    showCargoPolicyBanner,
  };
}
